import { useMemo, useState } from 'react';
import { useAppData } from '../app/AppProvider';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { LineDots } from '../components/LineDots';
import { PlaceCard } from '../components/PlaceCard';
import { TopBar } from '../components/TopBar';
import { METRO_STATION_BY_ID } from '../data/metro';
import type { PlaceStatus } from '../domain/types';

type StationFilter = 'all' | PlaceStatus;

export function StationPage({ stationId }: { stationId: string }) {
  const { data, setStationFavorite } = useAppData();
  const [filter, setFilter] = useState<StationFilter>('all');
  const station = METRO_STATION_BY_ID.get(stationId);
  const favorite = data.favoriteStationIds.includes(stationId);

  const places = useMemo(() => data.places
    .filter((place) => place.stationId === stationId && (filter === 'all' || place.status === filter))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt)), [data.places, filter, stationId]);

  const total = data.places.filter((place) => place.stationId === stationId).length;

  if (!station) {
    return (
      <main className="page">
        <TopBar title="Станция не найдена" />
        <EmptyState title="Такой станции нет">Возможно, ссылка устарела. Вернитесь на главный экран.</EmptyState>
      </main>
    );
  }

  return (
    <main className="page">
      <TopBar title="Станция" />
      <section className="station-hero">
        <div className="station-hero__heading">
          <div>
            <h1>{station.name}</h1>
            <div className="station-hero__meta"><LineDots lines={station.lines} /><span>{total} {pluralPlaces(total)}</span></div>
          </div>
          <button
            className={`icon-button ${favorite ? 'icon-button--active' : ''}`}
            type="button"
            aria-label={favorite ? 'Убрать станцию из избранного' : 'Добавить станцию в избранное'}
            aria-pressed={favorite}
            onClick={() => void setStationFavorite(stationId, !favorite)}
          >
            <Icon name="star" />
          </button>
        </div>
      </section>

      <div className="segmented" aria-label="Фильтр мест">
        {([['all', 'Все'], ['want', 'Хочу посетить'], ['visited', 'Был']] as const).map(([value, label]) => (
          <button className="segmented__button" key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
        ))}
      </div>

      <section className="section" style={{ marginTop: 18 }}>
        {places.length > 0 ? (
          <div className="card-list">{places.map((place) => <PlaceCard key={place.id} place={place} />)}</div>
        ) : (
          <EmptyState title={total === 0 ? 'На станции пока нет мест' : 'Нет мест с таким статусом'}>
            {total === 0 ? 'Добавьте место — станция уже будет выбрана в форме.' : 'Выберите другой раздел списка.'}
          </EmptyState>
        )}
      </section>

      <a className="fab" href={`#/place/new?station=${encodeURIComponent(station.id)}`}><Icon name="plus" /> Добавить место</a>
    </main>
  );
}

function pluralPlaces(value: number): string {
  const mod100 = value % 100;
  const mod10 = value % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'мест';
  if (mod10 === 1) return 'место';
  if (mod10 >= 2 && mod10 <= 4) return 'места';
  return 'мест';
}
